import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, TextFieldProps } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';

interface AnimatedPlaceholderProps {
    animationPhrases: string[]; // Phrases for the animated placeholder
    typingSpeed?: number; // Typing speed in ms
    pauseBetweenPhrases?: number; // Pause duration in ms
    textFieldProps?: TextFieldProps; // Additional props for the TextField
}

const AnimatedTextInput: React.FC<AnimatedPlaceholderProps> = ({
    animationPhrases,
    typingSpeed = 100,
    pauseBetweenPhrases = 2000,
    textFieldProps = {},
}) => {
    const [placeholder, setPlaceholder] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [showCaret, setShowCaret] = useState(true); // Toggle caret visibility
    const timeOutRef = useRef<any>(null);
    const { themeStyle } = useThemeContext();

    useEffect(() => {
        if (isTyping) return;
        if (timeOutRef.current) clearTimeout(timeOutRef.current);

        let phraseIndex = 0;
        let charIndex = 0;
        let typingTimeout: NodeJS.Timeout | null = null;

        const resetAnimation = () => {
            charIndex = 0;
            phraseIndex = 0;
            setPlaceholder('');
        };

        const typeNextCharacter = () => {
            if (phraseIndex >= animationPhrases.length) return;

            const currentPhrase = animationPhrases[phraseIndex];

            if (charIndex <= currentPhrase.length) {
                setPlaceholder(currentPhrase.slice(0, charIndex));
                charIndex++;
                timeOutRef.current = setTimeout(typeNextCharacter, typingSpeed);
            } else {
                timeOutRef.current = setTimeout(() => {
                    charIndex = 0; // Reset character index
                    phraseIndex = (phraseIndex + 1) % animationPhrases.length; // Move to next phrase
                    typeNextCharacter(); // Start typing the next phrase
                }, pauseBetweenPhrases);
            }
        };

        resetAnimation(); // Reset the animation on phrases change
        typeNextCharacter(); // Start the animation

        return () => {
            if (typingTimeout) clearTimeout(timeOutRef.current); // Cleanup timeout on unmount
        };
    }, [animationPhrases, typingSpeed, pauseBetweenPhrases, isTyping]);

    useEffect(() => {
        const caretInterval = setInterval(() => {
            setShowCaret(prev => !prev); // Toggle caret visibility
        }, 500); // Change caret visibility every 500ms

        return () => {
            clearInterval(caretInterval); // Cleanup interval on unmount
        };
    }, []);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!isTyping) {
            setIsTyping(true); // Stop animation when typing starts
            setPlaceholder(''); // Clear the placeholder
        }
        if (textFieldProps.onChange) {
            textFieldProps.onChange(event); // Call user-provided onChange handler
        }
    };

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            <TextField
                {...textFieldProps}
                placeholder={!isTyping ? `${placeholder}${showCaret ? '|' : ' '}` : ''}
                onChange={handleInputChange}
                multiline
                fullWidth
                variant="outlined"
                sx={{
                    ...textFieldProps?.sx,
                    '& .MuiOutlinedInput-root': {
                        position: 'relative', // Ensure caret visibility
                        '& fieldset': {
                            borderColor: theme[themeStyle].grey,
                        },
                        '&:hover fieldset': {
                            borderColor: theme[themeStyle].highlight,
                        },
                    },
                }}
            />
        </Box>
    );
};

export default AnimatedTextInput;
